import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Chip from "@mui/material/Chip"
import FormControlLabel from "@mui/material/FormControlLabel"
import ListItemText from "@mui/material/ListItemText"
import MenuItem from "@mui/material/MenuItem"
import Switch from "@mui/material/Switch"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { format, subDays } from "date-fns"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import AdminEntityAutocomplete, {
  ENTITY_RESOLVE,
} from "./AdminEntityAutocomplete"
import AdminLiquidToolbar from "./AdminLiquidToolbar"

const ANY = ""

export const DATE_RANGE_PRESETS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
]

/** "from~to" (either side optional) <-> {from, to}. */
export function parseDateRangeValue(value) {
  const [from = "", to = ""] = String(value || "").split("~")
  return { from, to }
}

export function formatDateRangeValue({ from = "", to = "" } = {}) {
  if (!from && !to) return ""
  return `${from}~${to}`
}

/** toParams for a daterange filter: "a~b" -> {<prefix>_from: a, <prefix>_to: b}. */
export function dateRangeToParams(prefix) {
  return (value) => {
    const { from, to } = parseDateRangeValue(value)
    return {
      ...(from ? { [`${prefix}_from`]: from } : {}),
      ...(to ? { [`${prefix}_to`]: to } : {}),
    }
  }
}

export function splitMulti(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
}

function presetRange(days) {
  const today = new Date()
  return {
    from: format(subDays(today, Number(days) - 1), "yyyy-MM-dd"),
    to: format(today, "yyyy-MM-dd"),
  }
}

function matchingPreset(value) {
  const { from, to } = parseDateRangeValue(value)
  return DATE_RANGE_PRESETS.find((preset) => {
    const range = presetRange(preset.value)
    return range.from === from && range.to === to
  })?.value
}

function optionLabel(filter, value) {
  return (
    (filter.options || []).find((option) => option.value === value)?.label ||
    value
  )
}

function useEntityLabels(filters, values) {
  const [labels, setLabels] = useState({})
  const pending = filters
    .filter((filter) => filter.type === "entity")
    .map((filter) => [filter, values[filter.key] || ""])
    .filter(
      ([filter, value]) =>
        value &&
        !(filter.staticOptions || []).some((o) => String(o.id) === value) &&
        !labels[`${filter.entity}:${value}`]
    )
  const pendingKey = pending
    .map(([filter, value]) => `${filter.entity}:${value}`)
    .join("|")

  useEffect(() => {
    if (!pendingKey) return
    let cancelled = false
    pending.forEach(([filter, value]) => {
      Promise.resolve()
        .then(() => ENTITY_RESOLVE[filter.entity](value))
        .then((label) => {
          if (cancelled || !label) return
          setLabels((current) => ({
            ...current,
            [`${filter.entity}:${value}`]: label,
          }))
        })
        .catch(() => {})
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey])

  function remember(entity, option) {
    if (!option || option.id == null) return
    setLabels((current) => ({
      ...current,
      [`${entity}:${option.id}`]: option.label || option.name || option.email,
    }))
  }

  return [labels, remember]
}

function entityOption(filter, value, labels) {
  if (!value) return null
  const staticOption = (filter.staticOptions || []).find(
    (option) => String(option.id) === value
  )
  if (staticOption) return staticOption
  return {
    id: value,
    label: labels[`${filter.entity}:${value}`] || `#${value}`,
  }
}

export function describeFilterValue(filter, value, labels = {}) {
  switch (filter.type) {
    case "multi":
      return splitMulti(value)
        .map((item) => optionLabel(filter, item))
        .join(", ")
    case "entity":
      return entityOption(filter, value, labels)?.label || value
    case "daterange": {
      const preset = matchingPreset(value)
      if (preset) return optionLabel({ options: DATE_RANGE_PRESETS }, preset)
      const { from, to } = parseDateRangeValue(value)
      return `${from || "…"} to ${to || "…"}`
    }
    case "switch":
      return filter.chipLabel || filter.label
    default:
      return optionLabel(filter, value)
  }
}

function DateRangeControl({ filter, value, onChange }) {
  const preset = value ? matchingPreset(value) || "custom" : ANY
  const [customOpen, setCustomOpen] = useState(preset === "custom")
  const { from, to } = parseDateRangeValue(value)
  const showCustom = customOpen || preset === "custom"

  return (
    <Row gap={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        select
        size="small"
        label={filter.label}
        value={showCustom ? "custom" : preset}
        onChange={(event) => {
          const next = event.target.value
          if (next === "custom") {
            setCustomOpen(true)
            return
          }
          setCustomOpen(false)
          onChange(next ? formatDateRangeValue(presetRange(next)) : "")
        }}
        sx={{ minWidth: filter.minWidth || 150 }}
      >
        <MenuItem value={ANY}>Any time</MenuItem>
        {DATE_RANGE_PRESETS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        <MenuItem value="custom">Custom…</MenuItem>
      </TextField>
      {showCustom ? (
        <>
          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={(event) =>
              onChange(formatDateRangeValue({ from: event.target.value, to }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(event) =>
              onChange(formatDateRangeValue({ from, to: event.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
        </>
      ) : null}
    </Row>
  )
}

function FilterControl({ filter, value, onChange, labels, rememberLabel }) {
  switch (filter.type) {
    case "switch":
      return (
        <FormControlLabel
          control={
            <Switch
              checked={value === filter.onValue}
              onChange={(event) =>
                onChange(
                  event.target.checked ? filter.onValue : filter.offValue ?? ""
                )
              }
            />
          }
          label={filter.label}
        />
      )
    case "multi": {
      const selected = splitMulti(value)
      return (
        <TextField
          select
          size="small"
          label={filter.label}
          value={selected}
          SelectProps={{
            multiple: true,
            renderValue: (items) =>
              items.map((item) => optionLabel(filter, item)).join(", "),
          }}
          onChange={(event) => {
            const next = event.target.value
            onChange((Array.isArray(next) ? next : [next]).join(","))
          }}
          sx={{ minWidth: filter.minWidth || 150, maxWidth: 260 }}
        >
          {filter.options.map((option) => (
            <MenuItem key={option.value} value={option.value} dense>
              <Checkbox
                size="small"
                checked={selected.includes(option.value)}
                sx={{ p: 0.5, mr: 1 }}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </TextField>
      )
    }
    case "entity":
      return (
        <AdminEntityAutocomplete
          entity={filter.entity}
          label={filter.label}
          placeholder={filter.placeholder || "Any"}
          staticOptions={filter.staticOptions}
          value={entityOption(filter, value, labels)}
          onChange={(option) => {
            rememberLabel(filter.entity, option)
            onChange(option ? String(option.id) : "")
          }}
          sx={{ minWidth: filter.minWidth || 220, flex: "0 1 280px" }}
        />
      )
    case "daterange":
      return (
        <DateRangeControl filter={filter} value={value} onChange={onChange} />
      )
    default:
      return (
        <TextField
          select
          size="small"
          label={filter.label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          sx={{ minWidth: filter.minWidth || 150 }}
        >
          {filter.anyLabel === null ? null : (
            <MenuItem value={ANY}>{filter.anyLabel || "Any"}</MenuItem>
          )}
          {filter.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )
  }
}

/**
 * Shared list toolbar: search, filters, removable filter chips, Clear all,
 * result count and the page's load error. Every value lives in the URL via
 * `useAdminPaginatedQuery({filters})`; `filters` here only describes the UI.
 */
export default function AdminListToolbar({
  query,
  searchLabel = "Search",
  noun = "results",
  filters = [],
  actions,
  hideSearch = false,
  showCount = true,
}) {
  const values = query.filters || {}
  const specs = query.filterSpecs || {}
  const [labels, rememberLabel] = useEntityLabels(filters, values)

  const nonDefault = filters.filter(
    (filter) =>
      (values[filter.key] ?? "") !== (specs[filter.key]?.default ?? "")
  )
  const canClear = nonDefault.length > 0 || Boolean(query.searchText)
  const chips = nonDefault.filter(
    (filter) => filter.type !== "switch" || filter.chip
  )
  const count = query.totalResultsSize ?? 0

  return (
    <Column gap={1}>
      <AdminLiquidToolbar>
        {hideSearch ? null : (
          <TextField
            size="small"
            label={searchLabel}
            value={query.searchText}
            onChange={(event) => query.setSearchText(event.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 240 }, flex: 1, maxWidth: 420 }}
          />
        )}
        {filters.map((filter) => (
          <FilterControl
            key={filter.key}
            filter={filter}
            value={values[filter.key] ?? ""}
            onChange={(next) => query.setFilter(filter.key, next)}
            labels={labels}
            rememberLabel={rememberLabel}
          />
        ))}
        {canClear ? (
          <Button size="small" onClick={() => query.clearFilters()}>
            Clear all
          </Button>
        ) : null}
        {actions}
      </AdminLiquidToolbar>
      {chips.length || showCount ? (
        <Row
          gap={1}
          sx={{ flexWrap: "wrap", alignItems: "center", minHeight: 24 }}
        >
          {chips.map((filter) => (
            <Chip
              key={filter.key}
              size="small"
              label={`${filter.label}: ${describeFilterValue(
                filter,
                values[filter.key],
                labels
              )}`}
              onDelete={() =>
                query.setFilter(filter.key, specs[filter.key]?.default ?? "")
              }
            />
          ))}
          {showCount && !query.isLoading && !query.loadError ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: chips.length ? 1 : 0 }}
              data-testid="admin-list-count"
            >
              {count.toLocaleString()} {noun}
            </Typography>
          ) : null}
        </Row>
      ) : null}
      {query.loadError ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => query.refresh()}
            >
              Retry
            </Button>
          }
        >
          {query.loadError}
        </Alert>
      ) : null}
    </Column>
  )
}

const filterShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["select", "multi", "switch", "entity", "daterange"]),
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ),
  entity: PropTypes.string,
  staticOptions: PropTypes.array,
  onValue: PropTypes.string,
  offValue: PropTypes.string,
  anyLabel: PropTypes.string,
  placeholder: PropTypes.string,
  minWidth: PropTypes.number,
  chip: PropTypes.bool,
  chipLabel: PropTypes.string,
})

AdminListToolbar.propTypes = {
  query: PropTypes.shape({
    searchText: PropTypes.string,
    setSearchText: PropTypes.func,
    filters: PropTypes.object,
    filterSpecs: PropTypes.object,
    setFilter: PropTypes.func,
    clearFilters: PropTypes.func,
    totalResultsSize: PropTypes.number,
    isLoading: PropTypes.bool,
    loadError: PropTypes.string,
    refresh: PropTypes.func,
  }).isRequired,
  searchLabel: PropTypes.string,
  noun: PropTypes.string,
  filters: PropTypes.arrayOf(filterShape),
  actions: PropTypes.node,
  hideSearch: PropTypes.bool,
  showCount: PropTypes.bool,
}

FilterControl.propTypes = {
  filter: filterShape.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  labels: PropTypes.object,
  rememberLabel: PropTypes.func,
}

DateRangeControl.propTypes = {
  filter: filterShape.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
}
